/**
 * Om tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const omTattoosPack: ToilePack = {
    slug: 'om-tattoos',
    category: 'tattoo',
    name: 'Om tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'lotus', label: 'Om and lotus', subject: 'the Om symbol ॐ rising from an open lotus flower, the crescent and dot (chandrabindu) above it drawn clearly'},
        {key: 'mandala', label: 'Om mandala', subject: 'the Om symbol ॐ at the centre of a round mandala of petals and dots'},
        {key: 'mantra', label: 'Om Namah Shivaya', subject: 'the Om symbol ॐ above the mantra lettered in Devanagari as "ॐ नमः शिवाय", one clean line of script'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'blackwork', label: 'Blackwork', style: 'a blackwork tattoo drawing, heavy solid black masses with the details cut out of them as bare paper', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a trishul', 'gold ink', 'wrist'],
    directionHint: 'try: a trishul',
    seo: {
        title: 'Om tattoo designs: Om and lotus, mandala, mantra',
        metaDescription: 'Nine Om tattoo designs: Om rising from a lotus, Om in a mandala and Om above the mantra Om Namah Shivaya, in three styles.',
        h1: 'Om tattoo designs: Om and lotus, Om in a mandala, Om Namah Shivaya',
        tagline: 'Three Om designs, each drawn in fine line, blackwork and dotwork on white paper.',
        intro: 'Om, written ॐ in Devanagari, is the sacred sound of Hinduism, worn by Hindus, Buddhists, Jains and yoga practitioners on the wrist, the neck or the forearm. The nine sheets show Om rising from a lotus, Om at the centre of a mandala, and Om above the mantra Om Namah Shivaya. Each is drawn as fine line, blackwork and dotwork. Type a different sacred word or twist, such as a trishul, and the same styles can be asked for.',
        useCases: [
            'a wrist or neck Om',
            'an Om set in a mandala for the forearm',
            'a mantra line for the back of the arm',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine Om designs: Om above a lotus, Om in a mandala and Om above the mantra, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Is the Devanagari script correct?', a: 'The mantra is lettered in Devanagari as Om Namah Shivaya, ॐ नमः शिवाय. Check the spelling with a teacher before a permanent piece.'},
            {q: 'What is the difference between the styles?', a: 'Fine line is one uniform hairline with little shading. Blackwork uses heavy solid black masses. Dotwork builds the shadows from dots.'},
        ],
    },
}
